import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import { AGENCY_PLANS, agencyPlanByAllocation, AGENCY_TRIAL_CREDITS, TOPUP_PACKS, CREDIT_COST, NEWS_WATCH_CHANNEL_COST, WEBSITE_AUDIT_AGENTS, type Tenant, type UsageMetric } from '@traaker/shared';
import { useTranslation, Trans } from 'react-i18next';
import { useCredits } from '../../contexts/CreditContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { BrandFavicon } from '../brand/BrandFavicon';
import { creditsService, type CreditBalance, type CreditUsage } from '../../services/credits';
import { api } from '../../services/api';
import PlanSelectionDialog from '../billing/PlanSelectionDialog';
import { formatDate, formatNumber } from '../../i18n/format';

const SELF_SERVE_TIERS = new Set(['basic', 'pro', 'elite']);

const fmtDate = (iso?: string) =>
  iso ? formatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const fmtNum = (n: number) => formatNumber(n);

/**
 * Settings → Facturation. Tier-aware billing home:
 *  - agency: a dark "hero" subscription card (plan · price · credit meter · next
 *    refill date), INLINE recharge packs, and an inline plan-upgrade row.
 *  - self-serve (basic/pro/elite): subscription state + start/manage + quota packs.
 *  - enterprise: read-only contract + manage/contact.
 */
export default function BillingSettings({ tenant }: { tenant: Tenant }) {
  const { t } = useTranslation();
  const { isAgency, balance, refresh } = useCredits();
  const { refreshProfile } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [packAdded, setPackAdded] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Returning from Stripe Checkout (?checkout=success) — the balance/runway is
  // granted by the invoice webhook, which may land a beat after the redirect.
  // Force a refresh so the just-bought credits + expiry show without waiting for
  // the 60s poll, and strip the query param so a manual reload doesn't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      const reload = () => { refresh(); refreshProfile(); }; // balance + plan/tier
      reload();
      const t = window.setTimeout(reload, 4000); // catch a slightly late webhook
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
      return () => window.clearTimeout(t);
    }
  }, [refresh, refreshProfile]);

  const isSelfServe = SELF_SERVE_TIERS.has(tenant.tier);
  const hasCustomer = tenant.billingMode === 'stripe' && !!tenant.stripeCustomerId;
  const isActive = tenant.billingStatus === 'active';
  const agencySubscribed = isAgency && isActive;
  const hasActiveSubscription =
    tenant.billingMode === 'stripe' && isActive && !!tenant.stripeSubscriptionId;

  const redirect = async (key: string, fn: () => Promise<string>) => {
    setError(null);
    setBusy(key);
    try {
      window.location.href = await fn();
    } catch (e: any) {
      setError(e?.message || t('billing.actionFailed'));
      setBusy(null);
    }
  };

  const manage = () =>
    redirect('manage', async () =>
      (await creditsService.portalSession(`${window.location.origin}/settings?tab=billing`)).url);

  // Agency plan: a NEW subscription returns a Stripe Checkout URL (redirect); a
  // plan CHANGE on a live sub swaps the Price in place (no redirect) → refresh
  // the balance so the new allocation + prorated credits appear.
  const agencyCheckout = async (planKey: string) => {
    setError(null);
    setNotice(null);
    setBusy(planKey);
    try {
      const res = await creditsService.agencyCheckout(planKey);
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      // Swapped in place. An UPGRADE flips the plan + grants credits via webhook;
      // a DOWNGRADE schedules at period end. Refresh BOTH the balance (credits)
      // AND the tenant profile (plan label "Agency S"→"M", "Changer d'offre"
      // status, pending-downgrade chip) — the webhook lands a beat later, so poll
      // both again after a short delay.
      setNotice(
        res.direction === 'downgrade'
          ? t('billing.changeScheduled', { date: fmtDate(res.effectiveAt) })
          : t('billing.planUpdated'),
      );
      const reload = () => Promise.all([refresh(), refreshProfile()]);
      await reload();
      window.setTimeout(reload, 4000);
      setBusy(null);
    } catch (e: any) {
      setError(e?.message || t('billing.actionFailed'));
      setBusy(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      {isAgency && (
        <AgencyBilling
          tenant={tenant}
          balance={balance}
          agencySubscribed={agencySubscribed}
          hasCustomer={hasCustomer}
          isActive={isActive}
          busy={busy}
          packAdded={packAdded}
          setPackAdded={setPackAdded}
          onCheckout={agencyCheckout}
          onTopup={(packKey) =>
            redirect(packKey, async () => (await creditsService.topupCheckout(packKey)).checkoutUrl)}
          onManage={manage}
        />
      )}

      {isSelfServe && (
        <SelfServeBilling
          tenant={tenant}
          hasCustomer={hasCustomer}
          isActive={isActive}
          hasActiveSubscription={hasActiveSubscription}
          busy={busy}
          onManage={manage}
        />
      )}

      {tenant.tier === 'enterprise' && (
        <Card variant="outlined">
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{t('billing.enterprise.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('billing.enterprise.body')}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {hasCustomer && isActive && (
                <Button variant="outlined" disabled={!!busy} onClick={manage} startIcon={<OpenInNewRoundedIcon />}>
                  {busy === 'manage' ? '…' : t('billing.manageSubscription')}
                </Button>
              )}
              <Button variant="text" href="https://traaker.ai/contact.html" target="_blank">{t('billing.contact')}</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <DataRetentionCard tenant={tenant} />
    </Box>
  );
}

/* ─────────────────────────── Data retention (RGPD) ─────────────────────────── */

/**
 * RGPD erasure opt-in. Lives on the billing panel — not on the cancel screen —
 * because Stripe's Customer Portal owns cancellation, so the only place we control
 * is here. Unticked (default) keeps the account 12 months after the subscription
 * ends so it can be reactivated; ticked purges everything 30 days after the end.
 * The API recomputes the purge date when the tenant is ALREADY cancelled, so the
 * choice stays actionable after the fact. Owner-only (it schedules a deletion).
 */
function DataRetentionCard({ tenant }: { tenant: Tenant }) {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const [checked, setChecked] = useState(!!tenant.settings?.deleteDataOnCancel);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isOwner = user?.role === 'owner';

  const save = async (next: boolean) => {
    setErr(null);
    setChecked(next); // optimistic — the switch must not lag the click
    setSaving(true);
    try {
      await api.patch('/tenants/current/data-retention', { deleteDataOnCancel: next });
      await refreshProfile(); // picks up the recomputed purgeDueAt
    } catch (e: any) {
      setChecked(!next);
      setErr(e?.message || t('billing.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" color="text.secondary">{t('billing.retention.eyebrow')}</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{t('billing.retention.title')}</Typography>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              disabled={saving || !isOwner}
              onChange={(e) => save(e.target.checked)}
            />
          }
          label={t('billing.retention.deleteOnCancel')}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {checked ? t('billing.retention.helpChecked') : t('billing.retention.helpUnchecked')}
        </Typography>
        {tenant.purgeDueAt && (
          <Alert severity={checked ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {t('billing.retention.scheduled', { date: fmtDate(tenant.purgeDueAt) })}
          </Alert>
        )}
        {!isOwner && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('billing.retention.ownerOnly')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Agency ─────────────────────────── */

function AgencyBilling({
  tenant, balance, agencySubscribed, hasCustomer, isActive, busy,
  packAdded, setPackAdded, onCheckout, onTopup, onManage,
}: {
  tenant: Tenant;
  balance: CreditBalance | null;
  agencySubscribed: boolean;
  hasCustomer: boolean;
  isActive: boolean;
  busy: string | null;
  packAdded: string | null;
  setPackAdded: (v: string | null) => void;
  onCheckout: (planKey: string) => void;
  onTopup: (packKey: string) => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const allocation = balance?.monthlyAllocation ?? tenant.settings?.agencyCredits?.monthlyAllocation ?? 0;
  const currentPlan = AGENCY_PLANS.find((p) => p.monthlyAllocation === allocation);
  const sub = balance?.subscriptionCredits ?? 0;
  const topup = balance?.topupCredits ?? 0;
  const reserved = balance?.reserved ?? 0;
  const available = balance?.available ?? 0;

  // Usage (per-brand + by-type + run-rate) — ONE call, feeds both the runway
  // burn-rate and the "Utilisation des crédits" donut. Informational: never
  // blocks the page (failure → undefined → runway shows no burn, donut hidden).
  const [usage, setUsage] = useState<CreditUsage | null>(null);
  useEffect(() => {
    let cancelled = false;
    creditsService.getUsage()
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <AgencyHero
        eyebrow={agencySubscribed ? t('billing.agency.subscriptionEyebrow') : t('billing.agency.trialEyebrow')}
        planLabel={agencySubscribed ? (currentPlan?.label ?? t('billing.agency.customPlan')) : t('billing.agency.trialCredits', { credits: AGENCY_TRIAL_CREDITS })}
        priceLine={
          agencySubscribed
            ? (currentPlan ? t('billing.agency.pricePerMonth', { price: fmtNum(currentPlan.priceEur) }) : t('billing.agency.negotiatedRate'))
            : t('billing.agency.noCommitment')
        }
        available={available}
        sub={sub}
        topup={topup}
        reserved={reserved}
        allocation={allocation}
        refillDate={agencySubscribed ? balance?.subscriptionExpireAt : undefined}
        endsAt={agencySubscribed && tenant.cancelAtPeriodEnd ? tenant.subscriptionEndsAt : undefined}
        dailyBurn={usage?.dailyBurn ?? 0}
        rightAction={hasCustomer && isActive ? <ManageLink busy={busy} onManage={onManage} /> : undefined}
      />

      {usage && usage.total > 0 && <AgencyUsageSection usage={usage} />}

      {/* Subscription — FIRST but quiet (changes are rare). When subscribed it's
          a collapsed "change plan" disclosure; on trial it's the subscribe prompt. */}
      <PlanSection
        agencySubscribed={agencySubscribed}
        allocation={allocation}
        currentPlan={currentPlan}
        periodEnd={balance?.subscriptionExpireAt}
        pendingAllocation={tenant.settings?.agencyCredits?.pendingMonthlyAllocation}
        pendingEffectiveAt={tenant.settings?.agencyCredits?.pendingEffectiveAt}
        showDemarrageOffer={!!tenant.settings?.featureFlags?.agencyDemarrageOffer}
        busy={busy}
        onCheckout={onCheckout}
      />

      {/* Recharge — quiet outlined tiles; the runway + donut are the loud elements.
          One-shot credit packs require a live Agency subscription (the API rejects
          a top-up without an active sub), so hide them on the credit-trial and
          nudge the user to subscribe first. */}
      {agencySubscribed && (
        <Box>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.25 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
              {t('billing.recharge.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('billing.recharge.subtitle')}
            </Typography>
          </Stack>
          {packAdded && <Alert severity="success" sx={{ mb: 2, mt: 1 }} onClose={() => setPackAdded(null)}>{t('billing.recharge.started')}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mt: 1.5 }}>
            {TOPUP_PACKS.map((p, i) => (
              <RechargeTile
                key={p.key}
                credits={p.credits}
                priceEur={p.priceEur}
                best={i === TOPUP_PACKS.length - 1}
                busy={busy === p.key}
                disabled={!!busy}
                onClick={() => onTopup(p.key)}
              />
            ))}
          </Box>
        </Box>
      )}

      <PricingReferenceSection />

      <Box data-tour="credit-ledger">
        <Button size="small" href="/credits/ledger" startIcon={<HistoryRoundedIcon />}>
          {t('billing.viewCreditHistory')}
        </Button>
      </Box>
    </>
  );
}

/* ─────────────────────────── Pricing reference (credit grid) ─────────────────────────── */

/** Blended €/crédit reference, derived from the entry Agency plan so it can't
 *  drift from the catalog (Agency S = priceEur / monthlyAllocation). */
const EUR_PER_CREDIT = AGENCY_PLANS[0].priceEur / AGENCY_PLANS[0].monthlyAllocation;
const eur = (credits: number) => `≈ ${formatNumber(credits * EUR_PER_CREDIT, { maximumFractionDigits: 0 })} €`;

/** Every billable action + its credit cost, derived from CREDIT_COST so the grid
 *  is always in lock-step with the real pricing. Each row carries a hue + icon so
 *  the table reads as a colored legend that matches the "par type d'action" panel. */
const PRICING_ROWS: {
  labelKey: string;
  detailKey: string;
  detailVars?: Record<string, number>;
  cost: number;
  unitKey: string;
  color: string;
  Icon: typeof PsychologyRoundedIcon;
}[] = [
  { labelKey: 'billing.pricing.engineCall.label', detailKey: 'billing.pricing.engineCall.detail', cost: CREDIT_COST.engine_call, unitKey: 'billing.pricing.engineCall.unit', color: '#7C3AED', Icon: PsychologyRoundedIcon },
  { labelKey: 'billing.pricing.website.label', detailKey: 'billing.pricing.website.detail', detailVars: { agents: WEBSITE_AUDIT_AGENTS }, cost: CREDIT_COST.page_analyzed * WEBSITE_AUDIT_AGENTS, unitKey: 'billing.pricing.website.unit', color: '#10B981', Icon: PublicRoundedIcon },
  { labelKey: 'billing.pricing.fetch.label', detailKey: 'billing.pricing.fetch.detail', cost: CREDIT_COST.fetch, unitKey: 'billing.pricing.fetch.unit', color: '#0EA5E9', Icon: NewspaperRoundedIcon },
  { labelKey: 'billing.pricing.competitor.label', detailKey: 'billing.pricing.competitor.detail', cost: CREDIT_COST.competitor, unitKey: 'billing.pricing.competitor.unit', color: '#F59E0B', Icon: GroupsRoundedIcon },
  { labelKey: 'billing.pricing.studio.label', detailKey: 'billing.pricing.studio.detail', cost: NEWS_WATCH_CHANNEL_COST, unitKey: 'billing.pricing.studio.unit', color: '#A78BFA', Icon: AutoAwesomeRoundedIcon },
  { labelKey: 'billing.pricing.report.label', detailKey: 'billing.pricing.report.detail', cost: CREDIT_COST.report_export, unitKey: 'billing.pricing.report.unit', color: '#0B0B12', Icon: DescriptionRoundedIcon },
  { labelKey: 'billing.pricing.productOptimize.label', detailKey: 'billing.pricing.productOptimize.detail', cost: CREDIT_COST.product_review, unitKey: 'billing.pricing.productOptimize.unit', color: '#EC4899', Icon: ShoppingBagRoundedIcon },
  { labelKey: 'billing.pricing.productMeasure.label', detailKey: 'billing.pricing.productMeasure.detail', cost: CREDIT_COST.engine_call, unitKey: 'billing.pricing.productMeasure.unit', color: '#EC4899', Icon: ShoppingBagRoundedIcon },
];

/** A single line of a usage example — a description, a cadence badge, and its
 *  monthly credit cost. `labelKey`/`labelVars` resolve to the display string at
 *  render; `cadence` is a stable code keying both the style map and its i18n label. */
type CadenceCode = 'quotidien' | 'hebdo' | 'biweekly' | 'mensuel';
type ExampleLine = { labelKey: string; labelVars?: Record<string, number>; cadence: CadenceCode; credits: number };
type ExampleProfile = { nameKey: string; taglineKey: string; accent: string; lines: ExampleLine[] };

const A = WEBSITE_AUDIT_AGENTS;             // 3 credits / page
const C = CREDIT_COST.competitor;           // 2 credits / competitor / axis
const CH = NEWS_WATCH_CHANNEL_COST;         // 2 credits / channel
const R = CREDIT_COST.report_export;        // 15 credits / report

/** Three worked usage profiles, from light to intensive. Every credit figure is
 *  computed from the constants above so the totals stay honest against the grid. */
const EXAMPLE_PROFILES: ExampleProfile[] = [
  {
    nameKey: 'billing.example.discovery.name',
    taglineKey: 'billing.example.discovery.tagline',
    accent: '#0EA5E9',
    lines: [
      { labelKey: 'billing.example.line.websiteAudit', labelVars: { pages: 15 }, cadence: 'mensuel', credits: 15 * A },
      { labelKey: 'billing.example.line.earnedMedia', labelVars: { sources: 40 }, cadence: 'mensuel', credits: 40 },
      { labelKey: 'billing.example.line.prompts', labelVars: { prompts: 15, engines: 3 }, cadence: 'hebdo', credits: 15 * 3 * 4 },
      { labelKey: 'billing.example.line.competitors', labelVars: { competitors: 3 }, cadence: 'mensuel', credits: 3 * 3 * C },
      { labelKey: 'billing.example.line.reports', labelVars: { reports: 1 }, cadence: 'mensuel', credits: R },
    ],
  },
  {
    nameKey: 'billing.example.growth.name',
    taglineKey: 'billing.example.growth.tagline',
    accent: '#7C3AED',
    lines: [
      { labelKey: 'billing.example.line.websiteAudit', labelVars: { pages: 50 }, cadence: 'hebdo', credits: 50 * A * 4 },
      { labelKey: 'billing.example.line.earnedMedia', labelVars: { sources: 80 }, cadence: 'hebdo', credits: 80 * 4 },
      { labelKey: 'billing.example.line.prompts', labelVars: { prompts: 20, engines: 3 }, cadence: 'quotidien', credits: 20 * 3 * 30 },
      { labelKey: 'billing.example.line.competitors', labelVars: { competitors: 10 }, cadence: 'hebdo', credits: 10 * 3 * C * 4 },
      { labelKey: 'billing.example.line.reports', labelVars: { reports: 4 }, cadence: 'mensuel', credits: 4 * R },
    ],
  },
  {
    nameKey: 'billing.example.intensive.name',
    taglineKey: 'billing.example.intensive.tagline',
    accent: '#F59E0B',
    lines: [
      { labelKey: 'billing.example.line.websiteAudit', labelVars: { pages: 100 }, cadence: 'hebdo', credits: 100 * A * 4 },
      { labelKey: 'billing.example.line.earnedMedia', labelVars: { sources: 100 }, cadence: 'hebdo', credits: 100 * 4 },
      { labelKey: 'billing.example.line.prompts', labelVars: { prompts: 50, engines: 4 }, cadence: 'quotidien', credits: 50 * 4 * 30 },
      { labelKey: 'billing.example.line.competitors', labelVars: { competitors: 25 }, cadence: 'hebdo', credits: 25 * 3 * C * 4 },
      { labelKey: 'billing.example.line.studio', labelVars: { creations: 20, channels: 3 }, cadence: 'mensuel', credits: 20 * 3 * CH },
      { labelKey: 'billing.example.line.reports', labelVars: { reports: 4 }, cadence: 'mensuel', credits: 4 * R },
    ],
  },
];

const profileTotal = (p: ExampleProfile) => p.lines.reduce((s, l) => s + l.credits, 0);

/** Cadence badge palette — a colored pill per rhythm so frequency reads at a glance. */
const CADENCE_STYLE: Record<CadenceCode, { bg: string; fg: string }> = {
  quotidien: { bg: '#FEE2E2', fg: '#B91C1C' },
  hebdo: { bg: '#E0F2FE', fg: '#0369A1' },
  biweekly: { bg: '#FEF3C7', fg: '#92660a' },
  mensuel: { bg: '#EDE9FE', fg: '#6D28D9' },
};

function CadenceBadge({ cadence }: { cadence: CadenceCode }) {
  const { t } = useTranslation();
  const s = CADENCE_STYLE[cadence] ?? { bg: '#F1F1F4', fg: '#52525B' };
  return (
    <Box
      component="span"
      sx={{
        px: 0.75, py: '2px', borderRadius: 99, flex: 'none',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.02em',
        textTransform: 'uppercase', bgcolor: s.bg, color: s.fg,
      }}
    >
      {t(`billing.cadence.${cadence}`)}
    </Box>
  );
}

/** A "Voir la grille tarifaire" button that opens a popup: a colored credit-cost
 *  table for every action + three worked monthly-consumption examples. */
function PricingReferenceSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)} startIcon={<CalculateRoundedIcon />}>
        {t('billing.pricing.openGrid')}
      </Button>
      <PricingDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}

function PricingDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Gradient header */}
      <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(120deg, #7C3AED 0%, #9460f0 55%, #0EA5E9 100%)', color: '#fff' }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <CalculateRoundedIcon />
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.01em', lineHeight: 1.1 }}>
              {t('billing.pricing.gridTitle')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t('billing.pricing.gridSubtitle')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Cost table — colored rows */}
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: 'hidden' }}>
          {PRICING_ROWS.map((row, i) => (
            <Stack
              key={row.labelKey}
              direction="row"
              alignItems="center"
              spacing={1.75}
              sx={{
                px: 2, py: 1.5,
                borderTop: i === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
                transition: 'background .15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 38, height: 38, borderRadius: 2, flex: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: `${row.color}1A`, color: row.color,
                }}
              >
                <row.Icon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>{t(row.labelKey)}</Typography>
                <Typography variant="caption" color="text.secondary">{t(row.detailKey, row.detailVars)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flex: 'none' }}>
                <Stack direction="row" alignItems="baseline" spacing={0.5} justifyContent="flex-end">
                  <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 800, fontSize: 26, lineHeight: 1, color: row.color }}>
                    {row.cost}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: row.color, opacity: 0.85 }}>{t('billing.crAbbr')}</Typography>
                </Stack>
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>{t(row.unitKey)}</Typography>
              </Box>
            </Stack>
          ))}
        </Card>

        {/* Worked examples */}
        <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.01em', mb: 0.25 }}>
          {t('billing.examples.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('billing.examples.subtitle', { rate: formatNumber(EUR_PER_CREDIT, { maximumFractionDigits: 3 }) })}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {EXAMPLE_PROFILES.map((p) => (
            <ExampleCard key={p.nameKey} profile={p} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>{t('billing.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ExampleCard({ profile }: { profile: ExampleProfile }) {
  const { t } = useTranslation();
  const total = profileTotal(profile);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Accent top bar + header */}
      <Box sx={{ height: 4, bgcolor: profile.accent }} />
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>{t(profile.nameKey)}</Typography>
        <Typography variant="caption" color="text.secondary">{t(profile.taglineKey)}</Typography>
      </Box>
      <Box sx={{ px: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {profile.lines.map((l) => (
          <Stack key={l.labelKey} direction="row" justifyContent="space-between" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', fontWeight: 500, lineHeight: 1.3 }}>{t(l.labelKey, l.labelVars)}</Typography>
              <CadenceBadge cadence={l.cadence} />
            </Box>
            <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 14, flex: 'none' }}>
              {fmtNum(l.credits)}
            </Typography>
          </Stack>
        ))}
      </Box>
      <Box sx={{ mt: 1.5, px: 2, py: 1.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: `${profile.accent}0D` }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="body2" fontWeight={700}>{t('billing.totalPerMonth')}</Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 800, fontSize: 24, letterSpacing: '-0.02em', color: profile.accent }}>
              {fmtNum(total)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: profile.accent }}>{t('billing.crAbbr')}</Typography>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', fontWeight: 600 }}>
          {t('billing.eurHtPerMonth', { amount: eur(total) })}
        </Typography>
      </Box>
    </Box>
  );
}

/** Subscription block: quiet for active agencies (collapsed change-plan), prominent on trial. */
function PlanSection({
  agencySubscribed, allocation, currentPlan, periodEnd, pendingAllocation, pendingEffectiveAt, showDemarrageOffer, busy, onCheckout,
}: {
  agencySubscribed: boolean;
  allocation: number;
  currentPlan?: { key: string; label: string; priceEur: number; monthlyAllocation: number };
  periodEnd?: string;
  pendingAllocation?: number;
  pendingEffectiveAt?: string;
  /** Per-tenant admin toggle surfacing the normally admin-only Agency Démarrage plan. */
  showDemarrageOffer?: boolean;
  busy: string | null;
  onCheckout: (planKey: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!agencySubscribed);
  // Plan the user clicked (active agency) — held until they confirm in the dialog.
  const [pending, setPending] = useState<(typeof AGENCY_PLANS)[number] | null>(null);

  // An active agency confirms before any change (upgrade charges now, downgrade
  // schedules at period end). A trial subscribe goes straight to checkout.
  const choose = (p: (typeof AGENCY_PLANS)[number]) => {
    if (agencySubscribed) setPending(p);
    else onCheckout(p.key);
  };

  // Admin-only plans (Agency Démarrage) are hidden from the customer picker by
  // default — they're provisioned from the admin page. A per-tenant admin toggle
  // (settings.featureFlags.agencyDemarrageOffer) can surface one on this specific
  // tenant's billing page for self-checkout. The reverse-lookups above still
  // resolve admin-only plans for display of a current plan regardless of the flag.
  // Agency XS is NO LONGER admin-only (2026-09-08) — it's the public entry plan,
  // so it needs neither the flag nor the promo styling.
  // Fixed-width columns (not 1fr) so tiles stay compact instead of
  // stretching to fill the row — matches the recharge-pack tile sizing below.
  const PROMO_PLAN_KEYS = new Set(['agency_demarrage_monthly']);
  const visiblePlans = AGENCY_PLANS.filter(
    (p) => !('adminOnly' in p && p.adminOnly) || (p.key === 'agency_demarrage_monthly' && showDemarrageOffer),
  );
  const plans = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(220px, 240px))' }, gap: 1.5, mt: agencySubscribed ? 2 : 0 }}>
      {visiblePlans.map((p) => {
        const current = agencySubscribed && p.monthlyAllocation === allocation;
        // Agency XS / Démarrage only ever appear here via their per-tenant
        // admin toggles — they're never part of the normal in-app catalog, so
        // flag them as promotional offers rather than presenting them as
        // regular tiers.
        const promo = PROMO_PLAN_KEYS.has(p.key);
        return (
          <PackTile
            key={p.key}
            headline={p.label}
            price={t('billing.agency.pricePerMonth', { price: fmtNum(p.priceEur) })}
            footnote={t('billing.agency.creditsPerMonth', { credits: fmtNum(p.monthlyAllocation) })}
            promo={promo}
            current={current}
            busy={busy === p.key}
            disabled={!!busy || current}
            cta={current ? t('billing.agency.currentPlan') : agencySubscribed ? t('billing.agency.change') : t('billing.agency.subscribe')}
            onClick={() => choose(p)}
          />
        );
      })}
    </Box>
  );

  const confirmDialog = (
    <ConfirmPlanChangeDialog
      open={!!pending}
      target={pending}
      currentAllocation={allocation}
      currentPriceEur={currentPlan?.priceEur}
      periodEnd={periodEnd}
      busy={!!busy}
      onClose={() => setPending(null)}
      onConfirm={() => { if (pending) onCheckout(pending.key); setPending(null); }}
    />
  );

  // Active agency: a low-key disclosure row (changes are infrequent).
  if (agencySubscribed) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpen((v) => !v)}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight={600}>{t('billing.agency.subscriptionLabel', { plan: currentPlan?.label ?? t('billing.agency.customPlanLower') })}</Typography>
                {currentPlan && PROMO_PLAN_KEYS.has(currentPlan.key) && (
                  <Chip size="small" label={t('billing.promoOffer')} color="warning" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {currentPlan ? t('billing.agency.priceAndCredits', { price: fmtNum(currentPlan.priceEur), credits: fmtNum(allocation) }) : t('billing.agency.negotiatedRate')}
              </Typography>
              {pendingAllocation != null && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontWeight: 700, color: '#92660a' }}>
                  {t('billing.agency.pendingChange', { plan: agencyPlanByAllocation(pendingAllocation)?.label ?? t('billing.agency.creditsShort', { credits: fmtNum(pendingAllocation) }), date: fmtDate(pendingEffectiveAt) })}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              variant="text"
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
            >
              {t('billing.agency.changePlan')}
            </Button>
          </Stack>
          <Collapse in={open} unmountOnExit>{plans}</Collapse>
          {confirmDialog}
        </CardContent>
      </Card>
    );
  }

  // Trial: prompt to subscribe.
  return (
    <SectionCard
      title={t('billing.agency.chooseSubscription')}
      subtitle={t('billing.agency.chooseSubscriptionSub')}
    >
      {plans}
    </SectionCard>
  );
}

/**
 * Confirmation before an agency plan change. Upgrade = instant, prorated charge
 * now + prorated extra credits now; downgrade = scheduled at period end, current
 * plan + credits kept until then. Shows an estimated prorated amount (30-day
 * basis, mirrors the backend prorateAllocation) so there are no surprises.
 */
function ConfirmPlanChangeDialog({
  open, target, currentAllocation, currentPriceEur, periodEnd, busy, onClose, onConfirm,
}: {
  open: boolean;
  target: (typeof AGENCY_PLANS)[number] | null;
  currentAllocation: number;
  currentPriceEur?: number;
  periodEnd?: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  if (!target) return null;
  const isUpgrade = target.monthlyAllocation > currentAllocation;
  // Fraction of a 30-day cycle still remaining (mirrors backend prorateAllocation).
  const now = Date.now();
  const endMs = periodEnd ? new Date(periodEnd).getTime() : now;
  const fraction = Math.min(1, Math.max(0, (endMs - now) / (30 * 86_400_000)));
  const proratedCredits = Math.round((target.monthlyAllocation - currentAllocation) * fraction);
  const proratedCharge = Math.round((target.priceEur - (currentPriceEur ?? 0)) * fraction);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isUpgrade ? t('billing.confirm.upgradeTitle') : t('billing.confirm.changeTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <Trans
            i18nKey="billing.confirm.summary"
            values={{ plan: target.label, price: fmtNum(target.priceEur), credits: fmtNum(target.monthlyAllocation) }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        {isUpgrade ? (
          <Box sx={{ bgcolor: '#F5F3FF', borderRadius: 2, p: 2 }}>
            <DetailRow k={t('billing.confirm.effect')} v={t('billing.confirm.immediate')} />
            <DetailRow k={t('billing.confirm.proratedCharge')} v={t('billing.confirm.proratedChargeValue', { amount: fmtNum(Math.max(0, proratedCharge)) })} />
            <DetailRow k={t('billing.confirm.proratedCredits')} v={t('billing.confirm.proratedCreditsValue', { credits: fmtNum(Math.max(0, proratedCredits)) })} />
            <DetailRow k={t('billing.confirm.fullRate')} v={t('billing.confirm.onDate', { date: fmtDate(periodEnd) })} />
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#FFFBEB', borderRadius: 2, p: 2 }}>
            <DetailRow k={t('billing.confirm.effect')} v={t('billing.confirm.onDate', { date: fmtDate(periodEnd) })} />
            <DetailRow k={t('billing.confirm.untilThen')} v={t('billing.confirm.currentKept')} />
            <DetailRow k={t('billing.confirm.noCharge')} v={t('billing.confirm.noRefund')} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{t('billing.cancel')}</Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}>
          {busy ? '…' : isUpgrade ? t('billing.confirm.confirmAndPay') : t('billing.confirm.scheduleChange')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{k}</Typography>
      <Typography variant="caption" fontWeight={700}>{v}</Typography>
    </Stack>
  );
}

/* ─────────────────────────── Self-serve ─────────────────────────── */

function SelfServeBilling({
  tenant, hasCustomer, isActive, hasActiveSubscription, busy, onManage,
}: {
  tenant: Tenant;
  hasCustomer: boolean;
  isActive: boolean;
  hasActiveSubscription: boolean;
  busy: string | null;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const trialing = tenant.billingStatus === 'trialing';
  const pending = tenant.billingStatus === 'pending';
  const [planOpen, setPlanOpen] = useState(false);

  const cancelPending = !!tenant.cancelAtPeriodEnd;
  const statusChip = cancelPending && isActive
    ? { label: t('billing.status.cancelScheduled'), color: 'warning' as const }
    : isActive
      ? { label: t('billing.status.active'), color: 'success' as const }
      : trialing
        ? { label: t('billing.status.trial'), color: 'warning' as const }
        : tenant.billingStatus === 'cancelled'
          ? { label: t('billing.status.cancelled'), color: 'default' as const }
          : { label: t('billing.status.toActivate'), color: 'default' as const };

  return (
    <>
      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="overline" color="text.secondary">{t('billing.subscription')}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ textTransform: 'capitalize', letterSpacing: '-0.02em' }}>
                {tenant.tier}
              </Typography>
            </Box>
            <Chip size="small" label={statusChip.label} color={statusChip.color} variant="filled" />
          </Stack>
          {trialing && tenant.trialEndsAt && (
            <StatRow icon={<AutorenewRoundedIcon fontSize="small" />} label={t('billing.trialEnd')} value={fmtDate(tenant.trialEndsAt)} />
          )}
          {cancelPending && tenant.subscriptionEndsAt && (
            <StatRow
              icon={<AutorenewRoundedIcon fontSize="small" />}
              label={t('billing.activeUntil')}
              value={fmtDate(tenant.subscriptionEndsAt)}
            />
          )}
          {tenant.billingStatus === 'cancelled' && tenant.subscriptionEndsAt && (
            <StatRow
              icon={<AutorenewRoundedIcon fontSize="small" />}
              label={t('billing.cancelledOn')}
              value={fmtDate(tenant.subscriptionEndsAt)}
            />
          )}
          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            {(trialing || pending) && (
              <Button variant="contained" disabled={!!busy} onClick={() => setPlanOpen(true)}>
                {t('billing.startSubscription')}
              </Button>
            )}
            {isActive && tenant.tier !== 'elite' && (
              <Button variant="contained" disabled={!!busy} onClick={() => setPlanOpen(true)}>
                {t('billing.changePlan')}
              </Button>
            )}
            {hasCustomer && isActive && (
              <Button variant="outlined" disabled={!!busy} onClick={onManage} startIcon={<OpenInNewRoundedIcon />}>
                {busy === 'manage' ? '…' : t('billing.manageSubscription')}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <PlanSelectionDialog
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        currentTier={tenant.tier}
        subscribed={hasActiveSubscription}
      />

      <QuotaCapacityCard onUpgrade={() => setPlanOpen(true)} canUpgrade={tenant.tier !== 'elite'} />
    </>
  );
}

/* ─────────────────────────── Quota capacity ─────────────────────────── */

/** Metrics worth surfacing as a capacity gauge, in display order. `prompts`
 *  leads (it's the metric that actually moves); the rest are compact gauges. */
const CAPACITY_METRICS = ['prompts', 'brands', 'competitors', 'users', 'zones', 'languages'] as const;
const LEAD_METRIC = 'prompts';

const isUnlimited = (limit: number) => limit < 0;
const pctOf = (used: number, limit: number) => (isUnlimited(limit) ? 0 : Math.min(100, (used / Math.max(limit, 1)) * 100));
const isMaxed = (m: UsageMetric) => !isUnlimited(m.limit) && m.used >= m.limit;

/**
 * Self-serve capacity panel — "am I outgrowing my plan?" Reads GET /metering/usage
 * (used + limit per metric, already resolved server-side: tier default + packs +
 * overrides). The lead gauge is prompts; secondary metrics render as compact
 * gauges that turn amber when a quota is reached. When anything is maxed, a
 * single upgrade nudge appears. No new endpoint — same payload the Marques tab uses.
 */
function QuotaCapacityCard({ onUpgrade, canUpgrade }: { onUpgrade: () => void; canUpgrade: boolean }) {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<UsageMetric[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ metrics: UsageMetric[] }>('/metering/usage')
      .then((u) => { if (!cancelled) setMetrics(u.metrics); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  if (failed) return null; // capacity is informational; never block the billing page on it
  if (!metrics) return <SectionCard title={t('billing.capacity.title')}><Box sx={{ height: 92 }} /></SectionCard>;

  const byKey = new Map(metrics.map((m) => [m.metric, m]));
  const lead = byKey.get(LEAD_METRIC);
  const secondary = CAPACITY_METRICS.filter((k) => k !== LEAD_METRIC)
    .map((k) => byKey.get(k))
    .filter((m): m is UsageMetric => !!m);
  const maxedOut = metrics.filter(isMaxed);

  return (
    <SectionCard title={t('billing.capacity.title')}>
      {lead && <LeadGauge metric={lead} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: { xs: 1.5, sm: '14px 28px' },
          mt: lead ? 2.5 : 0,
          pt: lead ? 2.25 : 0,
          borderTop: lead ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        {secondary.map((m) => <MiniGauge key={m.metric} metric={m} />)}
      </Box>

      {canUpgrade && maxedOut.length > 0 && (
        <Box
          sx={{
            mt: 2.5, p: 2, borderRadius: 2,
            border: '1px solid', borderColor: 'primary.light',
            bgcolor: 'primary.50',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {maxedOut.length === 1
                ? t('billing.capacity.quotaMaxedOne', { metric: maxedOut[0].label.fr.toLowerCase() })
                : t('billing.capacity.quotaMaxedMany', { count: maxedOut.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('billing.capacity.upgradeHint')}
            </Typography>
          </Box>
          <Button variant="contained" size="small" onClick={onUpgrade}>{t('billing.changePlan')}</Button>
        </Box>
      )}
    </SectionCard>
  );
}

/** The headline gauge — big fraction + full-width bar (amber when maxed). */
function LeadGauge({ metric }: { metric: UsageMetric }) {
  const { t } = useTranslation();
  const maxed = isMaxed(metric);
  const remaining = isUnlimited(metric.limit) ? null : Math.max(0, metric.limit - metric.used);
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="body2" fontWeight={600}>{metric.label.fr}</Typography>
        <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
          <Box component="span" sx={{ fontSize: 20 }}>{fmtNum(metric.used)}</Box>
          <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
            {isUnlimited(metric.limit) ? '/ ∞' : `/ ${fmtNum(metric.limit)}`}
          </Box>
        </Typography>
      </Stack>
      <GaugeBar pct={pctOf(metric.used, metric.limit)} maxed={maxed} height={12} />
      <Typography variant="caption" sx={{ color: maxed ? 'warning.dark' : 'text.secondary', fontWeight: maxed ? 600 : 400 }}>
        {isUnlimited(metric.limit)
          ? t('billing.capacity.unlimited')
          : maxed
            ? t('billing.capacity.limitReached')
            : t('billing.capacity.remaining', { value: fmtNum(remaining ?? 0), unit: metric.unit })}
      </Typography>
    </Box>
  );
}

/** A compact secondary gauge: label · fraction · thin bar. */
function MiniGauge({ metric }: { metric: UsageMetric }) {
  const maxed = isMaxed(metric);
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">{metric.label.fr}</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
          {fmtNum(metric.used)}
          <Box component="span" sx={{ color: 'text.disabled' }}>
            {isUnlimited(metric.limit) ? ' / ∞' : ` / ${fmtNum(metric.limit)}`}
          </Box>
        </Typography>
      </Stack>
      <GaugeBar pct={pctOf(metric.used, metric.limit)} maxed={maxed} height={7} />
    </Box>
  );
}

function GaugeBar({ pct, maxed, height }: { pct: number; maxed: boolean; height: number }) {
  return (
    <Box sx={{ mt: 0.75, height, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
      <Box
        sx={{
          width: `${pct}%`, height: '100%', borderRadius: 99,
          background: maxed
            ? 'linear-gradient(90deg, #F59E0B, #f0a93a)'
            : 'linear-gradient(90deg, #7C3AED, #9460f0)',
          transition: 'width .5s cubic-bezier(.2,.8,.2,1)',
        }}
      />
    </Box>
  );
}

/* ─────────────────────────── Agency usage (donut + by-type) ─────────────────────────── */

// Donut slice palette: top-5 named brands get a distinct hue; the rolled-up
// "Autres" wedge is neutral grey.
const BRAND_HUES = ['#7C3AED', '#10B981', '#0EA5E9', '#F59E0B', '#EC4899'] as const;
const AUTRES_HUE = '#D2D2DA';
const TOP_BRANDS = 5;

// i18n key + color for each credit primitive (by-type panel). Labels/subs
// resolve at render via `t()`.
const PRIMITIVE_META: Record<string, { labelKey: string; subKey: string; subVars?: Record<string, number>; color: string }> = {
  engine_call: { labelKey: 'billing.primitive.engineCall.label', subKey: 'billing.primitive.engineCall.sub', color: '#7C3AED' },
  page_analyzed: { labelKey: 'billing.primitive.pageAnalyzed.label', subKey: 'billing.primitive.pageAnalyzed.sub', color: '#10B981' },
  fetch: { labelKey: 'billing.primitive.fetch.label', subKey: 'billing.primitive.fetch.sub', color: '#0EA5E9' },
  competitor: { labelKey: 'billing.primitive.competitor.label', subKey: 'billing.primitive.competitor.sub', subVars: { cost: CREDIT_COST.competitor }, color: '#F59E0B' },
  news_query: { labelKey: 'billing.primitive.newsQuery.label', subKey: 'billing.primitive.newsQuery.sub', subVars: { cost: NEWS_WATCH_CHANNEL_COST }, color: '#A78BFA' },
  report_export: { labelKey: 'billing.primitive.reportExport.label', subKey: 'billing.primitive.reportExport.sub', subVars: { cost: CREDIT_COST.report_export }, color: '#0B0B12' },
  product_review: { labelKey: 'billing.primitive.productReview.label', subKey: 'billing.primitive.productReview.sub', subVars: { cost: CREDIT_COST.product_review }, color: '#EC4899' },
};

/**
 * "Utilisation des crédits" — per-brand donut (top-5 + Autres) beside a by-type
 * breakdown. Reads the pre-aggregated /credits/usage payload (no event scan);
 * brand names + favicons join from BrandContext. Scales flat to 150+ brands —
 * only the top 5 render as slices, the rest roll into one wedge.
 */
function AgencyUsageSection({ usage }: { usage: CreditUsage }) {
  const { t } = useTranslation();
  const { brands } = useBrand();
  const nameOf = (brandId: string) => brands.find((b) => b.brandId === brandId)?.name || t('billing.usage.brandFallback');

  const top = usage.byBrand.slice(0, TOP_BRANDS);
  const rest = usage.byBrand.slice(TOP_BRANDS);
  const restTotal = rest.reduce((s, b) => s + b.credits, 0);
  const total = usage.total || 1;

  // Build conic-gradient stops: top brands in order, then the Autres wedge.
  const stops: { color: string; label: string; value: number; brandId?: string; count?: number }[] = [
    ...top.map((b, i) => ({ color: BRAND_HUES[i], label: nameOf(b.brandId), value: b.credits, brandId: b.brandId })),
    ...(restTotal > 0 ? [{ color: AUTRES_HUE, label: t('billing.usage.otherBrands'), value: restTotal, count: rest.length }] : []),
  ];
  let acc = 0;
  const conic = stops
    .map((s) => {
      const from = (acc / total) * 100;
      acc += s.value;
      const to = (acc / total) * 100;
      return `${s.color} ${from}% ${to}%`;
    })
    .join(', ');

  const byTypeRows = Object.entries(usage.byType)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Box data-tour="agency-usage">
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>{t('billing.usage.title')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {t('billing.usage.thisMonth', { total: fmtNum(usage.total) })}
        </Typography>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 1.5 }}>
        {/* Donut par marque */}
        <Card variant="outlined">
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, display: 'block', mb: 2 }}>
              {t('billing.usage.byBrand')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
              <Box sx={{ position: 'relative', flex: 'none', width: 128, height: 128 }}>
                <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', background: `conic-gradient(${conic})` }} />
                <Box sx={{ position: 'absolute', inset: 23, borderRadius: '50%', bgcolor: 'background.paper', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 17, lineHeight: 1 }}>{usage.byBrand.length}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9.5 }}>{t('billing.usage.brands')}</Typography>
                </Box>
              </Box>
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                {stops.map((s) => (
                  <Stack key={s.brandId ?? 'autres'} direction="row" alignItems="center" spacing={1}>
                    {s.brandId
                      ? <BrandFavicon brandId={s.brandId} size={22} />
                      : <Box sx={{ width: 22, height: 22, borderRadius: '7px', bgcolor: AUTRES_HUE, color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700 }}>+{s.count}</Box>}
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: s.brandId ? 'text.primary' : 'text.secondary' }}>{s.label}</Typography>
                    <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: 12.5 }}>
                      {fmtNum(s.value)} <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>{Math.round((s.value / total) * 100)}%</Box>
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Par type d'action */}
        <Card variant="outlined">
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, display: 'block', mb: 2 }}>
              {t('billing.usage.byType')}
            </Typography>
            <Box sx={{ display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
              {byTypeRows.map(([k, v]) => (
                <Box key={k} sx={{ width: `${(v / total) * 100}%`, bgcolor: PRIMITIVE_META[k]?.color || '#9A9AA3' }} />
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px 28px' }}>
              {byTypeRows.map(([k, v]) => {
                const meta = PRIMITIVE_META[k];
                return (
                  <Stack key={k} direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: meta?.color || '#9A9AA3', flex: 'none' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{meta ? t(meta.labelKey) : k}</Typography>
                      {meta && <Typography variant="caption" color="text.disabled">{t(meta.subKey, meta.subVars)}</Typography>}
                    </Box>
                    <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: 14 }}>{fmtNum(v)}</Typography>
                  </Stack>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

/* ─────────────────────────── Shared pieces ─────────────────────────── */

/**
 * Light agency hero: balance + segmented source meter on the left, a runway
 * timeline (credits-to-refill, burn run-rate) on the right. The runway is the
 * signature — it answers "will my credits last until the next recharge?".
 */
function AgencyHero({
  eyebrow, planLabel, priceLine, available, sub, topup, reserved, allocation, refillDate, endsAt, dailyBurn, rightAction,
}: {
  eyebrow: string;
  planLabel: string;
  priceLine: string;
  available: number;
  sub: number;
  topup: number;
  reserved: number;
  allocation: number;
  refillDate?: string;
  /** Period-end date when a cancel is pending — overrides the renewal line. */
  endsAt?: string;
  dailyBurn: number;
  rightAction?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const scale = Math.max(allocation, sub + topup + reserved, 1);
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;

  // Runway: days the available balance lasts at the current run-rate vs days
  // until the refill. If runway ≥ days-to-refill → covered (green); else amber.
  const now = new Date();
  const refill = refillDate ? new Date(refillDate) : null;
  const daysToRefill = refill ? Math.max(0, Math.ceil((refill.getTime() - now.getTime()) / 86_400_000)) : null;
  const runwayDays = dailyBurn > 0 ? Math.floor(available / dailyBurn) : null;
  const covered = runwayDays != null && daysToRefill != null ? runwayDays >= daysToRefill : true;
  // Axis spans today → max(refill, runway end) so both markers fit.
  const axisDays = Math.max(daysToRefill ?? 0, runwayDays ?? 0, 1);
  const runwayPct = runwayDays != null ? `${Math.min(100, (runwayDays / axisDays) * 100)}%` : '0%';
  const refillPct = daysToRefill != null ? Math.min(100, (daysToRefill / axisDays) * 100) : null;

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {/* Balance + source meter */}
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.14em' }}>{eyebrow}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.1 }}>{planLabel}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{priceLine}</Typography>
              {endsAt ? (
                <Typography variant="caption" sx={{ mt: 0.75, display: 'block', fontWeight: 700, color: '#92660a' }}>
                  {t('billing.hero.cancelUntil', { date: fmtDate(endsAt) })}
                </Typography>
              ) : refillDate ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                  {t('billing.hero.nextRefill', { date: fmtDate(refillDate) })}
                </Typography>
              ) : null}
            </Box>
            {rightAction}
          </Stack>

          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 2.5 }}>
            <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.03em', fontFamily: 'ui-monospace, monospace' }}>
              {fmtNum(available)}
            </Typography>
            <Typography color="text.secondary">{t('billing.hero.creditsAvailable')}</Typography>
          </Stack>

          <Box sx={{ mt: 2, height: 10, borderRadius: 99, bgcolor: 'action.hover', display: 'flex', overflow: 'hidden' }}>
            <Box sx={{ width: pct(sub), bgcolor: '#7C3AED', transition: 'width .4s ease' }} />
            <Box sx={{ width: pct(topup), bgcolor: '#10B981', transition: 'width .4s ease' }} />
            <Box sx={{ width: pct(reserved), bgcolor: 'action.disabledBackground', transition: 'width .4s ease' }} />
          </Box>
          <Stack direction="row" spacing={2.5} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Legend dot="#7C3AED" label={t('billing.hero.legendSubscription')} value={fmtNum(sub)} />
            <Legend dot="#10B981" label={t('billing.hero.legendTopup')} value={fmtNum(topup)} />
            {reserved > 0 && <Legend dot="#C7C7CF" label={t('billing.hero.legendReserved')} value={fmtNum(reserved)} />}
          </Stack>
        </Box>

        {/* Runway */}
        <Box sx={{ p: { xs: 2.5, sm: 3 }, bgcolor: '#FCFCFD', borderLeft: { sm: '1px solid' }, borderTop: { xs: '1px solid', sm: 'none' }, borderColor: { xs: 'divider', sm: 'divider' } }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.14em' }}>{t('billing.hero.runway')}</Typography>
          {refill ? (
            <>
              <Box sx={{ mt: 1, mb: 2 }}>
                <Chip
                  size="small"
                  icon={covered ? <CheckRoundedIcon /> : <AutorenewRoundedIcon />}
                  label={covered ? t('billing.hero.covered') : t('billing.hero.rechargeAdvised')}
                  sx={{
                    fontWeight: 700,
                    bgcolor: covered ? '#ECFDF5' : '#FEF3C7',
                    color: covered ? '#059669' : '#92660a',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Box>
              <Box sx={{ position: 'relative', height: 14, borderRadius: 99, bgcolor: 'action.hover', overflow: 'visible' }}>
                <Box sx={{ position: 'absolute', inset: 0, width: runwayPct, borderRadius: 99, background: covered ? 'linear-gradient(90deg,#7C3AED,#9460f0)' : 'linear-gradient(90deg,#F59E0B,#f0a93a)', transition: 'width .5s ease' }} />
                {refillPct != null && (
                  <Box sx={{ position: 'absolute', top: -5, bottom: -5, left: `${refillPct}%`, width: 2, bgcolor: 'text.primary', borderRadius: 1 }} />
                )}
              </Box>
              <Stack direction="row" spacing={3} sx={{ mt: 2.5 }}>
                <RunwayStat k={t('billing.hero.pace')} v={dailyBurn > 0 ? `~${fmtNum(dailyBurn)}` : '—'} unit={t('billing.hero.crPerDay')} />
                <RunwayStat k={t('billing.hero.margin')} v={runwayDays != null ? `≈ ${runwayDays}` : '—'} unit={t('billing.hero.days')} />
                <RunwayStat k={t('billing.hero.beforeRefill')} v={daysToRefill != null ? String(daysToRefill) : '—'} unit={t('billing.hero.days')} />
              </Stack>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t('billing.hero.subscribePrompt')}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
}

function RunwayStat({ k, v, unit }: { k: string; v: string; unit: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{k}</Typography>
      <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: 18, mt: 0.25 }}>
        {v} <Box component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>{unit}</Box>
      </Typography>
    </Box>
  );
}

function Legend({ dot, label, value }: { dot: string; label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />
      <Typography variant="caption" color="text.secondary">
        {label} · <Box component="strong" sx={{ color: 'text.primary', fontFamily: 'ui-monospace, monospace' }}>{value}</Box>
      </Typography>
    </Stack>
  );
}

function StatRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary">{label} :</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  );
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.25 }}>{subtitle}</Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Recharge pack — quiet outlined tile: big credit count, price, per-1k rate.
 * The largest pack is flagged "Meilleur tarif" with an ink border. Click anywhere
 * to start checkout. Kept deliberately calm so the runway + donut stay the focus.
 */
function RechargeTile({
  credits, priceEur, best, busy, disabled, onClick,
}: {
  credits: number;
  priceEur: number;
  best?: boolean;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const per1k = (priceEur / (credits / 1000)).toFixed(0);
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      sx={{
        position: 'relative',
        p: 2.25,
        borderRadius: 3.5,
        border: '1px solid',
        borderColor: best ? 'text.primary' : 'divider',
        bgcolor: 'background.paper',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !busy ? 0.6 : 1,
        transition: 'transform .15s ease, box-shadow .18s ease, border-color .15s',
        '&:hover': disabled ? {} : { transform: 'translateY(-2px)', borderColor: 'primary.main', boxShadow: '0 8px 24px rgba(124,58,237,0.12)' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {best && (
        <Chip size="small" label={t('billing.recharge.bestRate')} sx={{ position: 'absolute', top: -10, right: 14, height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: 'text.primary', color: 'background.paper' }} />
      )}
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 24, letterSpacing: '-0.02em', lineHeight: 1 }}>{fmtNum(credits)}</Typography>
        <Typography variant="caption" color="text.secondary">{t('billing.crAbbr')}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.75 }}>
        <Box component="strong" sx={{ fontFamily: 'ui-monospace, monospace' }}>{t('billing.recharge.priceHt', { price: fmtNum(priceEur) })}</Box>
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>{t('billing.recharge.perThousand', { price: per1k })}</Typography>
      <Button fullWidth variant={best ? 'contained' : 'outlined'} disabled={disabled} sx={{ mt: 1.75 }} endIcon={!busy ? <ArrowForwardRoundedIcon /> : undefined}>
        {busy ? '…' : t('billing.recharge.recharge')}
      </Button>
    </Box>
  );
}

/** Quiet "Gérer" link for the agency hero's top-right corner. */
function ManageLink({ busy, onManage }: { busy: string | null; onManage: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="text" size="small" disabled={!!busy} onClick={onManage} endIcon={<OpenInNewRoundedIcon fontSize="small" />}>
      {busy === 'manage' ? '…' : t('billing.manage')}
    </Button>
  );
}

/**
 * A selectable tile (plan / quota pack) — deliberately as flat and quiet as
 * RechargeTile (same radius/hover/typography). No "featured"/Populaire state
 * at all: every regular tier looks identical, nothing is singled out as
 * recommended. The ONLY accent left is `promo` (a plan surfaced as a limited
 * offer, e.g. Agency XS) and `current` (already subscribed) — each conveyed
 * with a single thin border tint + a small muted caption line, not a badge,
 * not a gradient, not bold color on the figures.
 */
function PackTile({
  headline, price, footnote, promo, current, busy, disabled, cta, onClick,
}: {
  headline: string;
  price: string;
  footnote?: string;
  /** Renders as a limited-time promotional offer rather than a regular catalog tier. */
  promo?: boolean;
  current?: boolean;
  busy?: boolean;
  disabled?: boolean;
  cta: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const borderColor = current ? 'success.main' : promo ? 'warning.main' : 'divider';
  return (
    <Box
      sx={{
        position: 'relative',
        p: 2.25,
        borderRadius: 3.5,
        border: '1px solid',
        borderColor,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform .15s ease, box-shadow .18s ease, border-color .15s',
        '&:hover': disabled ? {} : { transform: 'translateY(-2px)', borderColor: promo ? 'warning.main' : 'primary.main', boxShadow: '0 8px 24px rgba(124,58,237,0.12)' },
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.1em', fontWeight: 700 }}>{headline}</Typography>
      <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em', mt: 0.75, whiteSpace: 'nowrap' }}>{price}</Typography>
      {footnote && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{footnote}</Typography>}
      {promo && !current && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontWeight: 600 }}>
          {t('billing.promoOffer')}
        </Typography>
      )}
      <Button
        fullWidth
        size="small"
        variant="outlined"
        color={current ? 'success' : promo ? 'warning' : 'primary'}
        disabled={disabled}
        onClick={onClick}
        startIcon={current ? <CheckRoundedIcon /> : undefined}
        endIcon={!current && !busy ? <ArrowForwardRoundedIcon /> : undefined}
        sx={{ mt: 1.75 }}
      >
        {busy ? '…' : cta}
      </Button>
    </Box>
  );
}

